import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArchiveIcon, ExternalLinkIcon, EyeIcon } from 'lucide-react';
import { z } from 'zod';
import { DateInput } from '@/components/date-input';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreview } from '@/features/preview/use-preview';
import { SignedCopy } from '@/features/signed/signed-copy';
import { formatDate, SIGNED_DOC_KINDS, type SignedDocKind } from '@hr/shared';
import { uk } from '@/i18n/uk';
import { trpc } from '@/lib/trpc';
import { useDebounced } from '@/lib/use-debounced';

const KINDS = [
  'contract',
  'amendment',
  'act',
  'protocol',
  'register',
  'personnel',
  'candidate',
] as const;

export const Route = createFileRoute('/_app/documents')({
  validateSearch: z.object({
    q: z.string().max(200).optional().catch(undefined),
    kind: z.enum(KINDS).optional().catch(undefined),
    from: z.iso.date().optional().catch(undefined),
    to: z.iso.date().optional().catch(undefined),
    signed: z.enum(['yes', 'no']).optional().catch(undefined),
  }),
  component: DocumentsPage,
});

const isSignable = (kind: string): kind is SignedDocKind =>
  (SIGNED_DOC_KINDS as readonly string[]).includes(kind);

function DocumentsPage() {
  const openPreview = usePreview();
  const t = uk.archive;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useDebounced(search.q, 300);
  const list = useQuery(
    trpc.documents.search.queryOptions({
      q,
      kind: search.kind,
      from: search.from,
      to: search.to,
      signed: search.signed,
    }),
  );

  return (
    <>
      <PageHeader title={t.title} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput value={search.q ?? ''} onChange={(v) => setSearch({ q: v || undefined })} />
        <Select
          value={search.kind ?? 'all'}
          onValueChange={(v) =>
            setSearch({ kind: v === 'all' ? undefined : (v as (typeof KINDS)[number]) })
          }
        >
          <SelectTrigger className="w-52" aria-label={t.allKinds}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allKinds}</SelectItem>
            {KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {t.kinds[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={search.signed ?? 'all'}
          onValueChange={(v) =>
            setSearch({ signed: v === 'all' ? undefined : (v as 'yes' | 'no') })
          }
        >
          <SelectTrigger className="w-52" aria-label={uk.signed.filter}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {uk.signed.filter}: {uk.signed.filterAll.toLowerCase()}
            </SelectItem>
            <SelectItem value="yes">{uk.signed.filterYes}</SelectItem>
            <SelectItem value="no">{uk.signed.filterNo}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span>{uk.contests.from}</span>
          <DateInput
            className="w-36"
            value={search.from}
            onChange={(v) => setSearch({ from: v ?? undefined })}
          />
          <span>{uk.contests.to}</span>
          <DateInput
            className="w-36"
            value={search.to}
            onChange={(v) => setSearch({ to: v ?? undefined })}
          />
        </div>
      </div>
      {list.isPending ? (
        <Skeleton className="h-64" />
      ) : list.data?.length === 0 ? (
        <EmptyState icon={ArchiveIcon} title={t.empty} />
      ) : (
        <>
          <ul className="divide-y rounded-lg border">
            {list.data?.map((d) => (
              <li
                key={`${d.kind}:${d.id}`}
                className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
              >
                <Badge variant="outline" className="w-36 justify-center">
                  {t.kinds[d.kind]}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{d.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{d.subtitle}</div>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">{d.dateLabel}</span>
                {isSignable(d.kind) &&
                  (d.signed ? (
                    <button
                      type="button"
                      className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800 hover:underline dark:bg-emerald-900/40 dark:text-emerald-300"
                      title={uk.signed.view}
                      onClick={() => openPreview({ url: d.signed!.url })}
                    >
                      {uk.signed.filterYes}
                      {d.signed.signedAt ? ` ${formatDate(d.signed.signedAt)}` : ''}
                    </button>
                  ) : (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      {uk.signed.filterNo}
                    </span>
                  ))}
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={t.view}
                  title={t.view}
                  onClick={() => openPreview({ url: d.url })}
                >
                  <EyeIcon />
                </Button>
                {isSignable(d.kind) && (
                  <SignedCopy
                    kind={d.kind}
                    entityId={d.id}
                    title={d.title}
                    knownCount={d.signed?.count ?? 0}
                    compact
                  />
                )}
                {d.link && (
                  <Button variant="ghost" size="icon" className="size-8" asChild>
                    {/* Посилання з довільних маршрутів архіву: типізація TanStack тут надто сувора. */}
                    <Link
                      to={d.link.to as '/'}
                      params={d.link.params as never}
                      search={d.link.search as never}
                      aria-label={t.open}
                      title={t.open}
                    >
                      <ExternalLinkIcon />
                    </Link>
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {(list.data?.length ?? 0) >= 300 && (
            <p className="mt-2 text-xs text-muted-foreground">{t.limit}</p>
          )}
        </>
      )}
    </>
  );
}
